import React from "react";

const SHOP = {
  name:      "Mana Vastralu",
  tagline:   "Mana Vastralu - Mana Gurthimpu",
  ownerName: "Reshma",
  address:   "H No: 5-94/260, Srujanalaxmi Nagar,\nRoad number -7, phase -2, Patelguda,\nPatancheru, Hyderabad,\nTelangana, Pin Code: 502319",
  phone:     "+91 9390905464",
  instagram: "mana_vastralu",
};

const API = process.env.REACT_APP_API_URL || "https://manavastralu-backend.onrender.com";

const getImgUrl = (img) => {
  if (!img) return null;
  if (img.startsWith("http")) return img;
  if (img.startsWith("uploads/")) return `${API}/${img}`;
  return `${API}/uploads/${img}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" }) : "—";

// Formats a 10-digit phone number as "XXXXX XXXXX" to match the
// physical label's spacing. Leaves anything that isn't a clean
// 10-digit number untouched so we don't mangle odd/foreign formats.
const fmtPhone = (p) => {
  if (!p) return "";
  const digits = String(p).replace(/\D/g, "");
  if (digits.length === 10) return digits.replace(/(\d{5})(\d{5})/, "$1 $2");
  return p;
};

const getBase64 = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth || 200;
      c.height = img.naturalHeight || 200;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);   // no black behind transparent images
      ctx.drawImage(img, 0, 0);
      resolve(c.toDataURL("image/jpeg"));
    } catch { resolve(""); }
  };
  img.onerror = () => resolve("");
  img.src = src;
});

const buildHTML = (order, logoB64, itemImgs) => {
  const items     = order.products || order.items || [];
  const total     = Number(order.total_amount || order.totalAmount || 0);
  const shortId   = String(order._id).slice(-8).toUpperCase();
  const payMethod = order.payment_method || order.paymentMethod || "Online";
  const addr      = order.address || {};
  const date      = fmtDate(order.createdAt || order.created_at);

  const toName  = addr.fullName || addr.name || order.email?.split("@")[0] || "Customer";
  const toPhone = fmtPhone(addr.phone || "");
  const toLines = [
    addr.houseNo || addr.street,
    addr.landmark,
    addr.village || addr.city,
    addr.district,
    addr.state,
    addr.pincode ? `Pin code: ${addr.pincode}` : "",
  ].filter(Boolean);

  /* ── INLINE INSTAGRAM SVG (no broken image, works in print) ── */
  const instagramSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" style="vertical-align:middle;margin-right:3px;display:inline-block;">
    <defs>
      <linearGradient id="igGrad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" style="stop-color:#f09433"/>
        <stop offset="25%" style="stop-color:#e6683c"/>
        <stop offset="50%" style="stop-color:#dc2743"/>
        <stop offset="75%" style="stop-color:#cc2366"/>
        <stop offset="100%" style="stop-color:#bc1888"/>
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="5" ry="5" fill="url(#igGrad)"/>
    <rect x="2" y="2" width="20" height="20" rx="4" ry="4" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="0.5"/>
    <circle cx="12" cy="12" r="4.5" fill="none" stroke="white" stroke-width="1.8"/>
    <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
    <rect x="3" y="3" width="14" height="14" rx="4" fill="none" stroke="white" stroke-width="1.5"/>
  </svg>`;

  /* ── WHATSAPP SVG ── */
  const whatsappSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" style="vertical-align:middle;margin-right:3px;display:inline-block;">
    <circle cx="12" cy="12" r="12" fill="#25D366"/>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="white"/>
  </svg>`;

  /* Larger icon copies for the shipping label, sized to match its bigger text.
     The gradient id is renamed so it doesn't clash with the footer's icon. */
  const instagramSVGLabel = instagramSVG
    .replace('width="14" height="14"', 'width="14" height="14"')
    .replace(/igGrad/g, "igGradLabel");
  const whatsappSVGLabel = whatsappSVG
    .replace('width="14" height="14"', 'width="14" height="14"');

  /* ── REFINED GOLD CORNER — single thin flourish, matches physical label ──
     Simplified from the previous version: fewer overlapping leaf shapes,
     thinner strokes, lower opacity so it reads as a delicate accent rather
     than a heavy decorative block competing with the border. */
  const cornerSVG = (flip) => `<svg width="38" height="38" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg" style="display:block;transform:${flip};">
    <g fill="none" stroke="#c8a04a" stroke-width="1.3" opacity="0.85" stroke-linecap="round">
      <path d="M6,50 C10,36 22,28 16,8"/>
      <path d="M6,42 C12,30 26,24 22,6"/>
      <path d="M10,34 C18,24 32,20 30,4"/>
      <path d="M16,28 C24,20 36,16 38,4"/>
    </g>
    <g fill="#c8a04a" opacity="0.75">
      <circle cx="6"  cy="50" r="2"/>
      <circle cx="9"  cy="43" r="1.4"/>
      <circle cx="13" cy="36" r="1.1"/>
    </g>
  </svg>`;

  // Circular logo with floral border
  const logoCircle = logoB64
    ? `<img src="${logoB64}" style="width:130px;height:130px;border-radius:50%;object-fit:cover;border:2px solid #c8a04a;display:block;"/>`
    : `<div style="width:164px;height:164px;border-radius:50%;background:linear-gradient(135deg,#fdf6ee,#fae8d0);border:2px solid #c8a04a;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:12px;">
        <div style="font-family:'Playfair Display',Georgia,serif;font-size:30px;font-weight:700;color:#3d1a0e;line-height:1.1;">Mana<br/>Vastralu</div>
        <div style="font-size:11px;color:#c8a04a;letter-spacing:0.5px;margin-top:4px;">— Mana Gurthimpu —</div>
      </div>`;

  const itemRows = items.map((item, idx) => {
    const imgB64   = itemImgs[idx] || "";
    const size     = item.selectedSize || item.size || "—";
    const qty      = item.quantity || 1;
    const price    = Number(item.price || 0);
    const subtotal = price * qty;
    return `
    <tr style="border-bottom:1px solid #f0e0c8;">
      <td style="padding:8px 6px;text-align:center;vertical-align:middle;">
        ${imgB64
          ? `<img src="${imgB64}" style="width:56px;height:64px;object-fit:cover;border-radius:4px;border:1px solid #e8d0b0;display:block;margin:0 auto;"/>`
          : `<div style="width:56px;height:64px;background:#fdf6ee;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:24px;margin:0 auto;">🥻</div>`}
      </td>
      <td style="padding:8px 6px;vertical-align:middle;">
        <strong style="font-size:13px;color:#2a1a00;display:block;">${item.name}</strong>
        <span style="font-size:11px;color:#7a5a30;">Size: ${size}${item.color ? ` · ${item.color}` : ""}</span>
      </td>
      <td style="padding:8px 6px;text-align:center;vertical-align:middle;font-size:13px;color:#2a1a00;">${qty}</td>
      <td style="padding:8px 6px;text-align:right;vertical-align:middle;font-size:13px;color:#2a1a00;">₹${price.toLocaleString("en-IN")}</td>
      <td style="padding:8px 6px;text-align:right;vertical-align:middle;font-size:13px;font-weight:700;color:#8b5e1a;">₹${subtotal.toLocaleString("en-IN")}</td>
    </tr>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Label & Invoice #${shortId} — Mana Vastralu</title>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Dancing+Script:wght@700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  html,body{background:#fff !important}
  body{font-family:'DM Sans',Arial,sans-serif;color:#2a1a00;font-size:13px}
  .page{width:170mm;margin:0 auto;padding:8mm 8mm;background:#fff}   /* label + invoice share ONE compact width */

  /* ══ LABEL OUTER WRAPPER — single clean gold border, no nested rings ══
     Previous version stacked a 3px outer border + 1px inner border + two
     gradient strips, which read as a heavy "double ring" rather than the
     single crisp line on the physical label. Simplified to one border. */
  .label-outer {
    position: relative;
    width: 100%;               /* same width as the invoice below */
    margin: 0 0 20px;
    border: 1.5px solid #c8a04a;
    border-radius: 6px;
    overflow: hidden;
    background: #fffaf6;
  }

  /* Soft watercolour background — matches the real label's pink-cream wash */
  .label-card {
    position: relative;
    background: #fffaf6;
    display: grid;
    grid-template-columns: 1fr 190px;
    grid-template-rows: auto auto;
    min-height: 270px;
  }

  /* Corner absolute positions */
  .corner { position: absolute; z-index: 3; padding: 10px; }
  .corner-tl { top: 0; left: 0; }
  .corner-tr { top: 0; right: 0; }
  .corner-bl { bottom: 0; left: 0; }
  .corner-br { bottom: 0; right: 0; }

  /* Thin gold hairline accents top & bottom (subtle, not a thick block) */
  .gold-strip-top, .gold-strip-bot {
    height: 2px;
    background: #c8a04a;
    opacity: 0.7;
  }

  /* LEFT side */
  .label-left {
    position: relative;
    z-index: 1;
    padding: 24px 14px 10px 34px;   /* extra room so larger text clears the corner flourishes */
    border-right: 1.5px dashed rgba(200,160,74,0.5);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  /* FROM = ONE full-width row under the address (so it is never split / cut on the parcel) */
  .label-from {
    grid-column: 1 / -1;
    position: relative;
    z-index: 1;
    margin: 0 34px;
    padding: 8px 0 22px;
    border-top: 1.5px dashed rgba(200,160,74,0.55);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    color: #111;
  }
  .label-from .from-tag { font-weight:700;font-size:13px;color:#1a1008; }
  .label-from b { font-weight:700;color:#000; }
  /* Label fonts enlarged (~+20%) for easier reading by couriers */
  .addr-tag { font-size:17px;font-weight:700;color:#1a1008;margin-bottom:6px;letter-spacing:.02em; }
  .addr-name { font-size:19px;font-weight:700;color:#000;margin-bottom:5px; }
  .addr-line { font-size:16px;font-weight:600;color:#111;line-height:1.4; }
  .addr-phone { font-size:19px;font-weight:700;color:#000;margin-top:5px;display:flex;align-items:center;gap:4px; }
  /* FROM block: clearly readable, but smaller than the customer address */
  .from-block .addr-tag { font-size:18px; }
  .from-block .addr-name { font-size:20px; }
  .from-block .addr-line { font-size:18px;font-weight:500;line-height:1.45; }
  .from-block .addr-phone { font-size:20px;margin-top:4px; }
  .separator { border:none;border-top:1.5px dashed rgba(200,160,74,0.55);margin:0; }

  /* RIGHT side */
  .label-right {
    position: relative;
    z-index: 1;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .brand-info { font-size:11px;line-height:1.7;color:#7a5a30;text-align:center;line-height:2;margin-top:6px; }

  /* THANK YOU badge — exactly like physical (pink/lavender) */
  .thank-you-badge {
    background: #f5d0ea;
    border-radius: 8px;
    padding: 7px 10px;
    text-align: center;
    width: 100%;
    border: 1.5px solid rgba(180,100,150,0.35);
  }
  .thank-you-text {
    font-family:'Playfair Display',serif;
    font-size:19px;font-weight:700;color:#3d1a0e;
    letter-spacing:2px;display:block;
  }
  .thank-you-sub {
    font-family:'Dancing Script',cursive;
    font-size:15px;color:#5a2a3e;display:block;margin-top:2px;
  }

  /* ══ ORDER INFO BAR ══ */
  .order-bar {
    display:flex;background:#fdf6ee;border:1.5px solid #e8d5a3;
    border-radius:7px;overflow:hidden;margin-bottom:18px;
  }
  .obi { flex:1;padding:9px 13px;border-right:1px solid #e8d5a3; }
  .obi:last-child { border-right:none; }
  .obi-label { font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#9a7050;margin-bottom:3px; }
  .obi-value { font-size:13px;font-weight:600;color:#2a1a00; }
  .obi-value.gold { color:#c8a04a;font-family:'Playfair Display',serif; }

  /* ══ ITEMS TABLE ══ */
  .section-title { font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#9a7050;margin-bottom:8px; }
  table.items { width:100%;border-collapse:collapse;margin-bottom:14px; }
  table.items thead tr { background:#c8a04a;color:#fff; }
  table.items thead th { padding:9px 6px;text-align:left;font-size:11px;font-weight:600;letter-spacing:.04em; }
  table.items thead th:nth-child(3){text-align:center}
  table.items thead th:nth-child(4),
  table.items thead th:nth-child(5){text-align:right}

  /* ══ TOTALS ══ */
  .totals-wrap { display:flex;justify-content:flex-end;margin-bottom:16px; }
  .totals-box { width:240px;border:1.5px solid #e8d5a3;border-radius:8px;overflow:hidden; }
  .tr { display:flex;justify-content:space-between;padding:7px 12px;font-size:12px;color:#5a3a10;border-bottom:1px solid #f0e6d0; }
  .tr.grand { background:#c8a04a;color:#fff;font-size:15px;font-weight:700;font-family:'Playfair Display',serif; }

  /* ══ FOOTER ══ */
  .footer { border-top:2px solid #c8a04a;padding-top:10px;display:flex;justify-content:space-between;align-items:flex-end; }
  .footer-left { font-size:11px;color:#9a7050;line-height:1.7; }
  .thankyou { font-family:'Playfair Display',serif;font-style:italic;font-size:20px;color:#c8a04a;text-align:right; }
  .thankyou-sub { font-size:11px;color:#9a7050;text-align:right;margin-top:2px; }

  @media print {
    html,body{background:#fff !important}
    .page{margin:0 auto;padding:4mm 8mm}
    .no-print{display:none!important}
    @page{size:A4;margin:8mm}

    /* ══ LABEL-ONLY PRINT MODE ══
       Toggled by adding the "print-label-only" class to <body> before
       calling window.print(). Hides everything except the shipping
       label itself, so the customer-facing slip prints alone instead
       of the whole invoice page. */
    body.print-label-only .order-bar,
    body.print-label-only .section-title,
    body.print-label-only table.items,
    body.print-label-only .totals-wrap,
    body.print-label-only .payment-tracking-box,
    body.print-label-only .footer {
      display: none !important;
    }
    body.print-label-only .label-outer {
      margin-bottom: 0;
    }
  }
</style>
</head>
<body>

<!-- Toolbar -->
<div class="no-print" style="background:#1a1008;padding:12px 20px;display:flex;gap:10px;align-items:center;position:sticky;top:0;z-index:100;box-shadow:0 2px 8px rgba(0,0,0,.4);">
  <span style="color:#c8a04a;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;flex:1;">🥻 Mana Vastralu — Label & Invoice #${shortId}</span>
  <button onclick="document.body.classList.add('print-label-only'); window.print();"
    style="background:#c8a04a;color:#1a1008;border:none;padding:9px 18px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;">
    🏷️ Print Label Only
  </button>
  <button onclick="document.body.classList.remove('print-label-only'); window.print();"
    style="background:#fff;color:#1a1008;border:1.5px solid #c8a04a;padding:9px 18px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;">
    🖨️ Print Full Invoice
  </button>
  <button onclick="window.close()" style="background:rgba(198,40,40,.15);color:#f87171;border:1px solid rgba(198,40,40,.3);padding:9px 16px;border-radius:6px;font-size:13px;cursor:pointer;">✕ Close</button>
</div>

<div class="page">

  <!-- ══ SHIPPING LABEL ══ -->
  <div class="label-outer">
    <div class="gold-strip-top"></div>

    <div class="label-card">

      <!-- Corner decorations — thin flourish matching real physical label -->
      <div class="corner corner-tl">${cornerSVG("none")}</div>
      <div class="corner corner-tr">${cornerSVG("scaleX(-1)")}</div>
      <div class="corner corner-bl">${cornerSVG("scaleY(-1)")}</div>
      <div class="corner corner-br">${cornerSVG("scale(-1,-1)")}</div>

      <!-- LEFT: Addresses -->
      <div class="label-left">
        <!-- TO -->
        <div>
          <div class="addr-tag">TO,</div>
          <div class="addr-name">${toName},</div>
          ${toLines.map(l => `<div class="addr-line">${l},</div>`).join("")}
          ${toPhone ? `<div class="addr-phone">📞: ${toPhone}.</div>` : ""}
        </div>

      </div>

      <!-- RIGHT: Brand Circle + social + thank you -->
      <div class="label-right">
        <div style="text-align:center;">
          ${logoCircle}
          <div class="brand-info">
            <div style="font-size:10.5px;color:#5a3a22;letter-spacing:0.3px;margin-bottom:4px;font-style:italic;">${SHOP.tagline}</div>
            <!-- Instagram with inline SVG logo — no broken image -->
            <div style="display:flex;align-items:center;justify-content:center;gap:4px;font-weight:600;color:#c13584;font-size:13px;">
              ${instagramSVGLabel}
              <span>${SHOP.instagram}</span>
            </div>
            <!-- WhatsApp with inline SVG logo -->
            <div style="display:flex;align-items:center;justify-content:center;gap:4px;font-weight:600;color:#2e7d32;font-size:13px;margin-top:2px;">
              ${whatsappSVGLabel}
              <span>${fmtPhone(SHOP.phone)}</span>
            </div>
            <div style="font-size:10px;color:#9a7050;margin-top:4px;font-style:italic;">Thanks for your support</div>
          </div>
        </div>

        <!-- THANK YOU badge (pink/lavender like physical label) -->
        <div class="thank-you-badge">
          <span class="thank-you-text">THANK YOU</span>
          <span class="thank-you-sub">for your purchase</span>
        </div>
      </div>


      <!-- FROM: one full-width row -->
      <div class="label-from">
        <span class="from-tag">From,</span>
        <b>${SHOP.name.toUpperCase()}</b>, (${SHOP.ownerName}), ${SHOP.address.replace(/\n/g, " ").replace(/Road number -7, phase -2/i,"Road Number -7, Phase -2")}
        &nbsp;<b>📞 ${fmtPhone(SHOP.phone)}</b>
      </div>
    </div><!-- /.label-card -->

    <div class="gold-strip-bot"></div>
  </div><!-- /.label-outer -->

  <div style="height:16px;"></div>

  <!-- ══ ORDER INFO BAR ══ -->
  <div class="order-bar">
    <div class="obi">
      <div class="obi-label">Order ID</div>
      <div class="obi-value gold">#${shortId}</div>
    </div>
    <div class="obi">
      <div class="obi-label">Date</div>
      <div class="obi-value" style="font-size:12px;">${date}</div>
    </div>
    <div class="obi">
      <div class="obi-label">Payment</div>
      <div class="obi-value" style="font-size:12px;">${payMethod}</div>
    </div>
    <div class="obi">
      <div class="obi-label">Total Amount</div>
      <div class="obi-value gold">₹${total.toLocaleString("en-IN")}</div>
    </div>
    <div class="obi">
      <div class="obi-label">Status</div>
      <div class="obi-value" style="color:#22c55e;font-size:12px;">✓ Confirmed</div>
    </div>
  </div>

  <!-- ══ ITEMS ══ -->
  <div class="section-title">🛍️ Items Ordered</div>
  <table class="items">
    <thead>
      <tr>
        <th style="width:68px;">Image</th>
        <th>Product</th>
        <th style="width:48px;text-align:center;">Qty</th>
        <th style="width:88px;text-align:right;">Price</th>
        <th style="width:96px;text-align:right;">Subtotal</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  <!-- ══ TOTALS ══ -->
  <div class="totals-wrap">
    <div class="totals-box">
      <div class="tr"><span>Subtotal</span><span>₹${total.toLocaleString("en-IN")}</span></div>
      // <div class="tr"><span>Shipping</span><span style="color:#22c55e;font-weight:600;"></span></div>
      <div class="tr grand"><span>Total Payable</span><span>₹${total.toLocaleString("en-IN")}</span></div>
    </div>
  </div>

  <!-- Payment & Tracking -->
  ${order.paymentId || order.trackingNumber ? `
  <div class="payment-tracking-box" style="display:flex;gap:24px;margin-bottom:14px;font-size:11px;color:#7a5a30;padding:10px 14px;background:#fdf6ee;border-radius:6px;border:1px solid #e8d5a3;">
    ${order.paymentId ? `<div><strong style="color:#9a7050;display:block;font-size:9px;text-transform:uppercase;letter-spacing:.08em;margin-bottom:2px;">Payment ID</strong><span style="font-family:monospace;color:#2a1a00;">${order.paymentId}</span></div>` : ""}
    ${order.trackingNumber ? `<div><strong style="color:#9a7050;display:block;font-size:9px;text-transform:uppercase;letter-spacing:.08em;margin-bottom:2px;">Tracking</strong><span style="color:#c8a04a;font-weight:600;">${order.courierName||"DTDC"} · ${order.trackingNumber}</span></div>` : ""}
  </div>` : ""}

  <!-- ══ FOOTER ══ -->
  <div class="footer">
    <div class="footer-left">
      <strong style="color:#8b5e1a;font-size:13px;">${SHOP.name}</strong><br/>
      <span style="display:inline-flex;align-items:center;gap:3px;">${instagramSVG} ${SHOP.instagram}</span>
      &nbsp;·&nbsp;
      <span style="display:inline-flex;align-items:center;gap:3px;">${whatsappSVG} ${fmtPhone(SHOP.phone)}</span><br/>
      manavastralu@gmail.com<br/>
      <span style="font-size:10px;color:#b0a090;">Computer generated — no signature required.</span>
    </div>
    <div>
      <div class="thankyou">Thank You! 💛</div>
      <div class="thankyou-sub">for your purchase & support</div>
    </div>
  </div>

</div>
</body>
</html>`;
};

export default function InvoiceGenerator({ order }) {
  const openInvoice = async () => {
    // Load logo + all item images as base64 in parallel
    const [logoB64, ...itemImgs] = await Promise.all([
      getBase64("/images/manavastralu_logo.jpeg"),
      ...(order.products || order.items || []).map(item => {
        const src = item.image || item.images?.[0] || "";
        const url = src ? getImgUrl(src) : "";
        return url ? getBase64(url) : Promise.resolve("");
      }),
    ]);

    const html = buildHTML(order, logoB64, itemImgs);
    const blob = new Blob([html], { type:"text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    if (!win) alert("Popup blocked! Please allow popups for this site.");
  };

  return (
    <button
      onClick={openInvoice}
      title="Print shipping label & invoice"
      style={{
        display:"flex", alignItems:"center", gap:6,
        padding:"7px 14px",
        background:"linear-gradient(135deg,#8b5e1a,#c8a04a)",
        color:"#fff", border:"none", borderRadius:6,
        fontSize:11, fontWeight:700, cursor:"pointer",
        fontFamily:"'DM Sans',sans-serif",
        boxShadow:"0 2px 8px rgba(139,94,26,.35)",
        transition:"opacity .2s,transform .2s",
        letterSpacing:"0.04em", whiteSpace:"nowrap",
      }}
      onMouseOver={e=>{e.currentTarget.style.opacity=".88";e.currentTarget.style.transform="translateY(-1px)"}}
      onMouseOut={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.transform="translateY(0)"}}
    >
      🖨️ Print Label & Invoice
    </button>
  );
}